import React, { FC } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

interface IProps {
  type: string;
  data: KwhDatum[] | ThermsDatum[] | null;
}

export interface KwhDatum {
  label: string;
  kwh: number;
  kwh_cost: number;
  dollar_per_kwh: number;
  kwh_per_sqft: number;
  kwh_dollar_per_sqft: number;
  kwh_eui: number;
}

export interface ThermsDatum {
  label: string;
  therms: number;
  therms_cost: number;
  dollar_per_therm: number;
  therms_per_sqft: number;
  therms_dollar_per_sqft: number;
  therms_eui: number;
}

const AnnualEnergyTargetDataTable: FC<IProps> = (props) => {
  const columns: any = [
    {
      id: "label",
      accessorKey: "label",
      header: "",
      cell: (props: any) => {
        return <div className="center-all">{props.getValue()}</div>;
      },
      size: 120,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id: props.type === "kwh" ? "kwh" : "therms",
      accessorKey: props.type === "kwh" ? "kwh" : "therms",
      header: props.type === "kwh" ? "kWh" : "Therms",
      cell: (props: any) => {
        return (
          <div className="center-all">
            {props.getValue().toLocaleString("en", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </div>
        );
      },
      size: 120,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id: props.type === "kwh" ? "kwh_cost" : "therms_cost",
      accessorKey: props.type === "kwh" ? "kwh_cost" : "therms_cost",
      header: props.type === "kwh" ? "kWh Cost" : "Therms Cost",
      cell: (props: any) => {
        return (
          <div className="center-all">
            $
            {props.getValue().toLocaleString("en", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </div>
        );
      },
      size: 120,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id: props.type === "kwh" ? "dollar_per_kwh" : "dollar_per_therm",
      accessorKey: props.type === "kwh" ? "dollar_per_kwh" : "dollar_per_therm",
      header: props.type === "kwh" ? " $/kWh" : "$/Therm",
      cell: (props: any) => {
        return <div className="center-all">${props.getValue().toFixed(2)}</div>;
      },
      size: 100,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id: props.type === "kwh" ? "kwh_per_sqft" : "therms_per_sqft",
      accessorKey: props.type === "kwh" ? "kwh_per_sqft" : "therms_per_sqft",
      header: props.type === "kwh" ? "kWh/ft²" : "Therms/ft²",
      cell: (props: any) => {
        return <div className="center-all">{props.getValue().toFixed(2)}</div>;
      },
      size: 100,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id:
        props.type === "kwh" ? "kwh_dollar_per_sqft" : "therms_dollar_per_sqft",
      accessorKey:
        props.type === "kwh" ? "kwh_dollar_per_sqft" : "therms_dollar_per_sqft",
      header: props.type === "kwh" ? "$/ft²" : `$/ft²`,
      cell: (props: any) => {
        return <div className="center-all">${props.getValue().toFixed(2)}</div>;
      },
      size: 150,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
    {
      id: props.type === "kwh" ? "kwh_eui" : "therms_eui",
      accessorKey: props.type === "kwh" ? "kwh_eui" : "therms_eui",
      header: props.type === "kwh" ? "kWh EUI" : "Therms EUI",
      cell: (props: any) => {
        return <div className="center-all">{props.getValue().toFixed(2)}</div>;
      },
      size: 150,
      enableColumnFilter: false,
      enableGlobalFilter: false,
    },
  ];

  const table = useReactTable<KwhDatum | ThermsDatum>({
    data: (props.data ?? []) as (KwhDatum | ThermsDatum)[],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div
      style={{
        width: "100%",
        marginTop: "0.5rem",
        display: "flex",
        justifyContent: "center",
        textAlign: "center",
      }}
    >
      <div
        className="stories-table"
        style={{
          width: table.getTotalSize() + 5,
          marginBottom: "2em",
          overflowX: "auto",
          maxHeight: "550px",
        }}
      >
        {table.getHeaderGroups().map((headerGroup: any) => (
          <div key={headerGroup.id} className="tr">
            {headerGroup.headers.map((header: any) => {
              return (
                <div
                  key={header.id}
                  className="th"
                  style={{ width: header.getSize() }}
                >
                  {header.column.columnDef.header}
                </div>
              );
            })}
          </div>
        ))}

        {table.getRowModel().rows.map((row: any) => (
          <div
            key={row.id}
            className="tr"
            style={{ width: table.getTotalSize() }}
          >
            {row.getVisibleCells().map((cell: any) => {
              return (
                <div
                  key={cell.id}
                  className="td"
                  style={{ width: cell.column.getSize() }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnnualEnergyTargetDataTable;
